<div *ngIf="open" class="fixed inset-0 z-[1100] flex items-end sm:items-center justify-center bg-slate-900/50 sm:p-4" (click)="close()">
  <div class="bg-white w-full sm:max-w-4xl max-h-[92vh] sm:max-h-[88vh] flex flex-col rounded-t-2xl sm:rounded-2xl shadow-2xl overflow-hidden"
       (click)="$event.stopPropagation()" role="dialog" aria-modal="true" aria-label="Contact customer">

    <!-- Header -->
    <header class="shrink-0 px-4 py-3 border-b border-slate-200 flex items-center gap-3">
      <div class="w-8 h-8 shrink-0 rounded-lg grid place-items-center bg-blue-50 text-blue-600">
        <i class="fa-solid fa-paper-plane text-sm"></i>
      </div>
      <div class="min-w-0 flex-1">
        <h3 class="text-sm font-bold text-slate-900 truncate">Contact {{ userName || 'customer' }}</h3>
        <p class="text-[11px] text-slate-500 truncate">
          {{ userEmail }}
          <span *ngIf="lastContact" class="text-slate-400"> · last emailed {{ lastContact.when }} ({{ lastContact.label }})</span>
        </p>
      </div>
      <button type="button" class="w-8 h-8 shrink-0 grid place-items-center rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-700"
              (click)="close()" aria-label="Close"><i class="fa-solid fa-xmark"></i></button>
    </header>

    <div *ngIf="unsubscribed" class="shrink-0 px-4 py-1.5 text-[11px] bg-amber-50 text-amber-800 border-b border-amber-100">
      <i class="fa-solid fa-triangle-exclamation mr-1"></i>
      Opted out of marketing emails — keep this to a personal or support message.
    </div>

    <div *ngIf="loading" class="flex-1 grid place-items-center py-20"><ion-spinner name="crescent"></ion-spinner></div>

    <!-- Sent -->
    <div *ngIf="!loading && sentTo" class="flex-1 grid place-items-center py-16 text-center px-6">
      <div>
        <div class="mx-auto w-12 h-12 rounded-full grid place-items-center bg-emerald-50 text-emerald-600 mb-3">
          <i class="fa-solid fa-check text-lg"></i>
        </div>
        <p class="text-sm font-semibold text-slate-900">Sent to {{ sentTo }}</p>
        <p class="text-xs text-slate-500 mt-1">{{ replyToEmail ? 'Replies go to ' + replyToEmail : 'No reply-to — replies go nowhere' }}</p>
      </div>
    </div>

    <ng-container *ngIf="!loading && !sentTo">
      <!-- Mobile: write / preview switch -->
      <div class="sm:hidden shrink-0 flex gap-1 p-1.5 mx-4 mt-3 rounded-lg bg-slate-100">
        <button type="button" class="flex-1 h-7 text-xs font-semibold rounded-md"
                [ngClass]="mobileTab === 'write' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-500'"
                (click)="mobileTab = 'write'">Write</button>
        <button type="button" class="flex-1 h-7 text-xs font-semibold rounded-md"
                [ngClass]="mobileTab === 'preview' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-500'"
                (click)="mobileTab = 'preview'">Preview</button>
      </div>

      <div class="flex-1 min-h-0 overflow-y-auto sm:overflow-hidden grid sm:grid-cols-2">
        <!-- Editor -->
        <div class="min-h-0 sm:overflow-y-auto p-4 space-y-3" [ngClass]="mobileTab === 'write' ? 'block' : 'hidden sm:block'">

          <!-- Quick replies -->
          <div>
            <div class="flex items-center justify-between mb-1.5">
              <span class="text-[11px] font-semibold text-slate-600">Quick reply</span>
              <a routerLink="/ceo/emails" [queryParams]="{ tab: 'quick' }" (click)="close()"
                 class="text-[11px] font-semibold text-blue-600 hover:text-blue-800 cursor-pointer">Manage</a>
            </div>
            <div *ngIf="templates.length; else noTemplates" class="flex flex-wrap gap-1.5">
              <button *ngFor="let t of templates" type="button"
                      class="h-7 max-w-full px-2.5 text-xs font-medium rounded-full border truncate transition-colors"
                      [ngClass]="t._id === selectedTemplateId ? 'bg-blue-600 border-blue-600 text-white' : 'bg-white border-slate-200 text-slate-600 hover:border-blue-300 hover:text-blue-700'"
                      (click)="applyTemplate(t)" [title]="t.subject">{{ t.name }}</button>
            </div>
            <ng-template #noTemplates>
              <p class="text-[11px] text-slate-400">No quick replies yet — write one below and save it.</p>
            </ng-template>
          </div>

          <app-ceo-sender-picker [message]="message" [meta]="senderMeta" [compact]="true"></app-ceo-sender-picker>

          <label class="block">
            <span class="block text-[11px] font-semibold text-slate-600 mb-1">Subject</span>
            <input type="text" class="ceo-input" maxlength="200" [(ngModel)]="message.subject" placeholder="Subject" />
          </label>

          <div>
            <div class="flex items-center justify-between gap-1 mb-1">
              <span class="text-[11px] font-semibold text-slate-600">Message <span class="font-normal text-slate-400">· plain text, edit freely</span></span>
              <span class="flex items-center gap-1">
                <app-ceo-polish-button [text]="message.body" [userId]="userId" [compact]="true" (apply)="message.body = $event"></app-ceo-polish-button>
                <button type="button" class="h-6 px-1.5 text-[10px] font-semibold rounded text-slate-500 hover:bg-slate-100 hover:text-red-600 disabled:opacity-40"
                        [disabled]="!message.body" (click)="clearText(body)" title="Delete the text and start from scratch">
                  <i class="fa-solid fa-eraser mr-0.5"></i>Clear</button>
                <button type="button" class="h-6 px-1.5 text-[10px] font-semibold rounded border border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100"
                        (click)="insertName(body)" title="Insert the first name">+ {{ nameToken }}</button>
              </span>
            </div>
            <textarea #body rows="10" maxlength="20000"
                      class="w-full px-3 py-2 text-sm leading-relaxed bg-white border border-slate-300 rounded-lg text-slate-800 shadow-sm focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/25 resize-y"
                      [(ngModel)]="message.body"></textarea>
          </div>

          <!-- Template maintenance -->
          <div class="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px]">
            <button *ngIf="edited" type="button" class="font-semibold text-slate-600 hover:text-blue-700 disabled:opacity-40"
                    [disabled]="savingTemplate" (click)="saveTemplate(false)">
              <i class="fa-solid fa-floppy-disk mr-1"></i>Update "{{ selectedTemplate?.name }}"
            </button>
            <button *ngIf="!namingTemplate" type="button" class="font-semibold text-slate-600 hover:text-blue-700 disabled:opacity-40"
                    [disabled]="savingTemplate || !canSend" (click)="saveTemplate(true)">
              <i class="fa-solid fa-plus mr-1"></i>Save as quick reply
            </button>
            <span *ngIf="namingTemplate" class="flex items-center gap-1.5 w-full">
              <input type="text" class="ceo-input !h-7 !text-xs" maxlength="80" placeholder="Quick reply name"
                     [(ngModel)]="newTemplateName" (keyup.enter)="saveTemplate(true)" />
              <button type="button" class="h-7 px-2.5 text-xs font-semibold rounded-lg bg-slate-900 text-white disabled:opacity-40"
                      [disabled]="!newTemplateName.trim() || savingTemplate" (click)="saveTemplate(true)">Save</button>
              <button type="button" class="h-7 px-1.5 text-xs text-slate-500" (click)="namingTemplate = false">Cancel</button>
            </span>
            <span *ngIf="templateNotice" class="text-emerald-600">{{ templateNotice }}</span>
            <span class="flex-1"></span>
            <button *ngIf="selectedTemplate && !namingTemplate" type="button"
                    class="font-semibold transition-colors"
                    [ngClass]="confirmDeleteTemplate ? 'px-1.5 rounded bg-red-600 text-white' : 'text-slate-400 hover:text-red-600'"
                    (click)="deleteTemplate()">
              <i class="fa-solid fa-trash-can mr-1"></i>{{ confirmDeleteTemplate ? 'Delete "' + selectedTemplate.name + '"?' : 'Delete template' }}
            </button>
          </div>
        </div>

        <!-- Preview -->
        <div class="min-h-0 p-4 sm:pl-0 sm:overflow-y-auto" [ngClass]="mobileTab === 'preview' ? 'block' : 'hidden sm:block'">
          <app-ceo-email-preview kind="direct" [message]="message" [userId]="userId" [compact]="true"></app-ceo-email-preview>
          <p class="mt-2 text-[10px] text-slate-400">
            Plain text, no tracking, no unsubscribe footer.
            {{ replyToEmail ? 'Replies go to ' + replyToEmail + '.' : 'No reply-to — replies go nowhere.' }}
          </p>
        </div>
      </div>

      <!-- Footer -->
      <footer class="shrink-0 border-t border-slate-200 px-4 py-2.5 bg-slate-50/70">
        <p *ngIf="error" class="mb-2 text-xs text-red-600">{{ error }}</p>

        <div *ngIf="testOpen" class="mb-2 flex flex-wrap items-center gap-1.5">
          <input type="email" class="ceo-input !h-8 !text-xs flex-1 min-w-[180px]" placeholder="Send the test to…"
                 [(ngModel)]="testEmail" (keyup.enter)="sendTest()" />
          <button type="button" class="h-8 px-3 text-xs font-semibold rounded-lg border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-40"
                  [disabled]="testing || !testEmail.trim() || !canSend" (click)="sendTest()">
            <i class="fa-solid mr-1" [ngClass]="testing ? 'fa-circle-notch fa-spin' : 'fa-flask'"></i>Send test
          </button>
          <span *ngIf="testResult" class="w-full text-[11px]" [ngClass]="testResult.startsWith('Test sent') ? 'text-emerald-600' : 'text-red-600'">{{ testResult }}</span>
        </div>

        <div class="flex items-center gap-2">
          <button type="button" class="h-8 px-2.5 text-xs font-semibold rounded-lg text-slate-600 hover:bg-slate-200/60"
                  (click)="testOpen = !testOpen"><i class="fa-solid fa-flask mr-1"></i>Test</button>
          <span class="flex-1"></span>
          <button *ngIf="confirming" type="button" class="h-8 px-2.5 text-xs font-semibold text-slate-500 hover:text-slate-800"
                  (click)="confirming = false">Cancel</button>
          <button type="button"
                  class="h-8 px-3.5 text-xs font-semibold rounded-lg text-white transition-colors disabled:opacity-40"
                  [ngClass]="confirming ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-blue-600 hover:bg-blue-700'"
                  [disabled]="!canSend || sending" (click)="send()">
            <i class="fa-solid mr-1" [ngClass]="sending ? 'fa-circle-notch fa-spin' : 'fa-paper-plane'"></i>
            {{ confirming ? 'Confirm: send to ' + userEmail : 'Send' }}
          </button>
        </div>
      </footer>
    </ng-container>
  </div>
</div>
